import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} SellingPlanPickerRefs
 * @property {HTMLElement} pickerRoot
 */

/**
 * PDP subscription options: syncs with variant section HTML, toggles `selling_plan` for the buy-buttons form,
 * GSP price rows, and `properties[_discount]` (parity with legacy `snippets/product.liquid` ProductPricing +
 * conditional removal of `_discount`).
 *
 * @extends {Component<SellingPlanPickerRefs>}
 */
class SellingPlanPicker extends Component {
  connectedCallback() {
    super.connectedCallback();
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;
    section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.addEventListener('change', this.#onRadioChange);
    this.addEventListener('click', this.#onPlanClick);
    this.#syncGspPrices();
    this.#syncSellingPlanInputState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const section = this.closest('.shopify-section, dialog');
    section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = String(event.detail.data.newProduct.id);
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPicker = event.detail.data.html.querySelector(
      `selling-plan-picker[data-block-id="${this.dataset.blockId}"]`
    );
    const newRoot = newPicker?.querySelector('[ref="pickerRoot"]');
    const { pickerRoot } = this.refs;
    if (!newRoot || !pickerRoot) return;

    pickerRoot.innerHTML = newRoot.innerHTML;
    this.updatedCallback();
    this.#syncGspPrices();
    this.#syncSellingPlanInputState();
  };

  #onRadioChange = () => {
    this.#syncSellingPlanInputState();
  };

  /**
   * @param {MouseEvent} e
   */
  #onPlanClick = (e) => {
    const btn = e.target instanceof Element ? e.target.closest('[data-selling-plan-button]') : null;
    if (!btn || !this.contains(btn)) return;
    e.preventDefault();
    const id = btn.getAttribute('data-selling-plan-id');
    if (!id) return;

    this.querySelectorAll('[data-selling-plan-button]').forEach((b) => b.classList.remove('is-active'));
    btn.classList.add('is-active');

    const input = this.querySelector('input[name="selling_plan"]');
    if (input instanceof HTMLInputElement && !input.disabled) {
      input.value = id;
    }

    this.#pushSellingPlanParam(id);
  };

  /**
   * @param {string} planId
   */
  #pushSellingPlanParam(planId) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('selling_plan', planId);
      history.pushState(null, '', url.toString());
    } catch {
      /* ignore */
    }
  }

  #syncSellingPlanInputState() {
    const subscribeChecked = this.querySelector('input[type="radio"][value="subscribe"]:checked');
    const input = this.querySelector('input[name="selling_plan"]');
    if (!(input instanceof HTMLInputElement)) return;

    const region = this.querySelector('[data-selling-plan-frequency-region]');

    if (subscribeChecked) {
      input.disabled = false;
      this.classList.add('is-in-subscription');
      let active = this.querySelector('[data-selling-plan-button].is-active');
      if (!active) {
        active = this.querySelector('[data-selling-plan-button]');
        active?.classList.add('is-active');
      }
      const planId = active?.getAttribute('data-selling-plan-id');
      if (planId) input.value = planId;
      if (region instanceof HTMLElement) {
        region.inert = false;
        region.removeAttribute('aria-hidden');
      }
    } else {
      input.disabled = true;
      this.classList.remove('is-in-subscription');
      if (region instanceof HTMLElement) {
        region.inert = true;
        region.setAttribute('aria-hidden', 'true');
      }
    }
  }

  /**
   * Channel price visibility is CSS driven by product-specific cookies against
   * the `data-gsp-price` pairs -- see snippets/gsp-channel-boot.liquid. It used to
   * be toggled here from a server-rendered flag, which could not be right: Shopify
   * serves product pages from a cache that ignores the query string, so the server
   * never reliably knew the channel.
   *
   * All this has to do now is clear the `hidden` attribute Liquid sets on the
   * discounted span for non-eligible products, in case a section morph swaps an
   * eligible product in.
   */
  #syncGspPrices() {
    const rows = this.querySelector('.selling-plan-picker__rows');
    if (!rows) return;

    if (rows.getAttribute('data-gsp-eligible') !== 'true') return;

    for (const el of rows.querySelectorAll('[data-gsp-price]')) {
      el.removeAttribute('hidden');
    }
  }
}

if (!customElements.get('selling-plan-picker')) {
  customElements.define('selling-plan-picker', SellingPlanPicker);
}
